import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import { Box, Button, CircularProgress, TextField } from '@mui/material';
import { makeStyles } from '@material-ui/core/styles';
import { InfoOutlined } from '@mui/icons-material';
import { Controller, useForm } from 'react-hook-form';

import Modal, { ModalContents, ModalTitle, ModalTypography } from 'components/Modal/Modal';
import { selectUser } from 'entities/user/selectors';
import { Privileges } from 'utils/privileges';
import Img from 'retired/Img';
import documentSvg from 'assets/document.svg';
import { useRequestNonprofitNameChangeMutation } from 'generated/graphql';
import { showErrorAction } from 'containers/Notification/actions';
import config from 'config';
import { generateEmailLink, generateEncodedEmailBody } from 'utils/supportEmailTemplate';

const useStyles = makeStyles({ message: { flexGrow: 1 } });

interface FormData {
  newName: string;
  reason: string;
}

export const RequestNonprofitNameChange = () => {
  const styles = useStyles();
  const [success, setSuccess] = useState(false);

  const dispatch = useDispatch();

  const [requestNonprofitNameChange, { loading }] = useRequestNonprofitNameChangeMutation({
    onError: () => {
      dispatch(showErrorAction('Failed to submit a request'));
    },
  });

  const user = useSelector(selectUser);

  const [modalOpen, setModalOpen] = useState(false);

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors, isValid },
  } = useForm<FormData>({
    defaultValues: {
      newName: user?.organization.name || '',
      reason: '',
    },
    mode: 'onChange',
  });

  const newName = watch('newName');
  const isNewNameDifferent = newName !== user?.organization.name;
  const isCompany = user?.organization?.isCompany();

  if (!user || isCompany || !user?.hasPrivilege(Privileges.ORGANIZATION_MANAGE_SETTINGS)) {
    return null;
  }

  const onClose = () => setModalOpen(false);

  const onSubmit = async (data: FormData) => {
    const response = await requestNonprofitNameChange({
      variables: { newName: data.newName.trim(), reason: data.reason },
    });
    if ((response as { errors: unknown }).errors) {
      dispatch(showErrorAction('Failed to submit a request'));
    } else {
      setSuccess(true);
    }
  };

  const emailBody = `
I just submitted a request to change the name of "${user?.organization.name}" to "${newName}".

I would like to...

`;

  return (
    <>
      <Box mt={1}>
        <Alert
          icon={<InfoOutlined fontSize="inherit" />}
          severity="info"
          style={{ marginBottom: '16px' }}
          classes={styles}
        >
          <Box display="flex">
            <Button
              style={{ textDecoration: 'underline', fontWeight: 500, margin: -8 }}
              variant="text"
              color="inherit"
              onClick={() => {
                setModalOpen(true);
              }}
            >
              Request to change your nonprofit&apos;s name
            </Button>
          </Box>
        </Alert>
      </Box>
      {modalOpen && (
        <Modal style={{ zIndex: 1200 }} open={modalOpen} fullWidth onClose={onClose}>
          <ModalContents
            onClickClose={onClose}
            headerStyles={{ paddingTop: 32, paddingBottom: 32 }}
            header={
              <Img
                src={documentSvg}
                alt=""
                style={{
                  width: 600,
                  height: 145,
                }}
              />
            }
            body={
              <Box mb={4}>
                <ModalTitle>Request to change your nonprofit&apos;s name</ModalTitle>
                {success ? (
                  <>
                    <ModalTypography>
                      Thanks! Our team will review your submission as soon as possible. Our standard
                      review process takes 1-2 business days.
                    </ModalTypography>
                    <ModalTypography>
                      You will receive an email notification once the review is complete. If
                      approved, your nonprofit&apos;s name will be updated in our system, and you
                      will receive a confirmation email. If additional information is needed, we
                      will reach out to you directly via your listed email {user?.email}
                    </ModalTypography>
                    <ModalTypography>
                      If you have any questions or need further assistance, please contact{' '}
                      <a
                        href={generateEmailLink({
                          recipient: config.nonprofitSuccessTeamEmail,
                          subject: `Questions about renaming ${user.organization.name}`,
                          emailBody: generateEncodedEmailBody({ user, emailBody }),
                        })}
                      >
                        Nonprofit Success Team
                      </a>{' '}
                      for assistance.
                    </ModalTypography>
                  </>
                ) : (
                  <>
                    <ModalTypography>
                      To protect your nonprofit and your donors, we review all nonprofit name
                      requests before processing them. This review usually takes 1-2 business days,
                      and you will be notified by email when the name change is completed.
                    </ModalTypography>
                    <Box
                      display="flex"
                      flexDirection="column"
                      justifyContent="space-between"
                      rowGap="16px"
                    >
                      <TextField
                        disabled
                        value={user.organization.name}
                        label="Current name"
                        helperText="The the current name of your nonprofit in our system."
                        fullWidth
                        variant="standard"
                      />
                      <Controller
                        name="newName"
                        control={control}
                        rules={{
                          required: 'Please enter a new organization name',
                          maxLength: 300,
                          validate: (value) =>
                            value.trim() === ''
                              ? 'Please enter a new organization name'
                              : value.trim() !== user.organization.name ||
                                'New name must be different from the current name',
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="New name"
                            helperText={`What name would you like to rename “${user.organization.name}” to?`}
                            fullWidth
                            required
                            variant="standard"
                            error={Boolean(errors.newName)}
                          />
                        )}
                      />
                      <Controller
                        name="reason"
                        control={control}
                        rules={{
                          required: 'Please enter a reason for name change',
                          maxLength: 300,
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Reason"
                            helperText="Why do you want to change the nonprofit's name?"
                            fullWidth
                            required
                            variant="standard"
                            error={Boolean(errors.reason)}
                          />
                        )}
                      />
                    </Box>
                  </>
                )}
              </Box>
            }
            actions={
              <div>
                {loading ? (
                  <CircularProgress size={24} />
                ) : success ? (
                  <Button
                    color="primary"
                    type="button"
                    variant="outlined"
                    style={{ marginRight: 8 }}
                    onClick={onClose}
                  >
                    Close
                  </Button>
                ) : (
                  <Button
                    color="primary"
                    type="button"
                    variant="contained"
                    style={{ marginRight: 8 }}
                    onClick={handleSubmit(onSubmit)}
                    disabled={!isValid || !isNewNameDifferent}
                  >
                    Submit Request
                  </Button>
                )}
              </div>
            }
          />
        </Modal>
      )}
    </>
  );
};
